import type {
  AreaEntity,
  CenterLayerApiResponse,
  ConsolidatedCenterLayerData,
  EnrichedCareerEntity,
  JornadaDataEntity,
} from '@/domains/centerLayer/centerLayer.entity';
import type { DayName, RiskIndicator } from '@/domains/shared';

/**
 * Use Case para consolidar datos de CenterLayer agrupados solo por jornada
 * Cumple SRP: Solo se encarga de consolidar data de múltiples años/semestres en estructura por jornada
 *
 * Responsabilidades:
 * - Recorrer estructura año > semestre > jornada
 * - Consolidar indicadores de riesgo sumando valores bajo/medio/alto
 * - Consolidar áreas únicas con totales sumados
 * - Consolidar carreras únicas agregando campo 'jornada' para clave compuesta
 * - Generar estructura final: { diurno: {...}, vespertino: {...} }
 */
export class ConsolidateCenterLayerByJornadaUseCase {
  /**
   * Consolida indicadores de riesgo sumando valores de múltiples periodos
   * @param existingIndicators - Indicadores actuales consolidados
   * @param newIndicators - Nuevos indicadores a agregar
   * @returns Array de indicadores consolidados con sumas actualizadas
   */
  private consolidateRiskIndicators(
    existingIndicators: RiskIndicator[],
    newIndicators: RiskIndicator[],
  ): RiskIndicator[] {
    const indicatorMap = new Map<string, RiskIndicator>();

    // Inicializar con indicadores existentes
    for (const indicator of existingIndicators) {
      indicatorMap.set(indicator.nombre.toLowerCase(), { ...indicator });
    }

    // Sumar nuevos indicadores
    for (const newIndicator of newIndicators) {
      const key = newIndicator.nombre.toLowerCase();
      const existing = indicatorMap.get(key);

      if (existing) {
        existing.bajo += newIndicator.bajo;
        existing.medio += newIndicator.medio;
        existing.alto += newIndicator.alto;
      } else {
        indicatorMap.set(key, { ...newIndicator });
      }
    }

    return Array.from(indicatorMap.values());
  }

  /**
   * Consolida áreas sumando el total de carreras por área
   * @param existingAreas - Áreas actuales consolidadas
   * @param newAreas - Nuevas áreas a agregar
   * @returns Array de áreas consolidadas con totales sumados
   */
  private consolidateAreas(
    existingAreas: AreaEntity[],
    newAreas: AreaEntity[],
  ): AreaEntity[] {
    const areaMap = new Map<string, AreaEntity>();

    // Inicializar con áreas existentes
    for (const area of existingAreas) {
      areaMap.set(area.nombre, { ...area });
    }

    // Sumar nuevas áreas
    for (const newArea of newAreas) {
      const existing = areaMap.get(newArea.nombre);

      if (existing) {
        existing['total-carrera'] += newArea['total-carrera'];
      } else {
        areaMap.set(newArea.nombre, { ...newArea });
      }
    }

    return Array.from(areaMap.values());
  }

  /**
   * Consolida carreras sumando totales y agregando campo jornada
   * Usa clave compuesta: cod_carrera + jornada para identificar carreras únicas
   * @param existingCareers - Carreras actuales consolidadas (con jornada)
   * @param newCareers - Nuevas carreras a agregar (sin jornada)
   * @param jornada - Jornada a agregar en cada carrera
   * @returns Array de carreras consolidadas con campo jornada
   */
  private consolidateCareers(
    existingCareers: EnrichedCareerEntity[],
    newCareers: EnrichedCareerEntity[],
    jornada: DayName,
  ): EnrichedCareerEntity[] {
    const careerMap = new Map<string, EnrichedCareerEntity>();

    // Inicializar con carreras existentes
    for (const career of existingCareers) {
      const key = `${career.cod_carrera}_${career.jornada}`;
      careerMap.set(key, { ...career });
    }

    // Sumar nuevas carreras con campo jornada
    for (const newCareer of newCareers) {
      const cod_compuesto = `${newCareer.cod_carrera}_${jornada}`;
      const existing = careerMap.get(cod_compuesto);

      if (existing) {
        existing['total-estudiante'] += newCareer['total-estudiante'];
        existing['estudiantes-con-riesgo'] +=
          newCareer['estudiantes-con-riesgo'];
      } else {
        careerMap.set(cod_compuesto, {
          ...newCareer,
          jornada,
          cod_compuesto,
        });
      }
    }

    return Array.from(careerMap.values());
  }

  /**
   * Inicializa estructura vacía para una jornada
   * @returns Objeto con estructura base de jornada
   */
  private createEmptyJornadaData(): JornadaDataEntity & {
    carreras: EnrichedCareerEntity[];
  } {
    return {
      'indicador-riesgo': [],
      areas: [],
      carreras: [],
    };
  }

  /**
   * Consolida data de todos los años/semestres en estructura por jornada únicamente
   * Procesa toda la data filtrada y genera resultado consolidado
   * @param apiResponse - Respuesta filtrada de la API (sin años futuros)
   * @returns Data consolidada agrupada solo por jornada (diurno/vespertino)
   */
  execute(apiResponse: CenterLayerApiResponse): ConsolidatedCenterLayerData {
    const consolidatedData: ConsolidatedCenterLayerData & {
      diurno?: JornadaDataEntity & { carreras: EnrichedCareerEntity[] };
      vespertino?: JornadaDataEntity & { carreras: EnrichedCareerEntity[] };
    } = {};

    // Validación de entrada
    if (!apiResponse?.data || typeof apiResponse.data !== 'object') {
      return consolidatedData;
    }

    const { data } = apiResponse;

    // Recorrer todos los años
    for (const yearKey in data) {
      const yearData = data[yearKey];

      // Recorrer todos los semestres del año
      for (const semesterKey in yearData) {
        const semesterData = yearData[semesterKey];

        // Recorrer todas las jornadas del semestre
        for (const jornadaKey in semesterData) {
          const jornada = jornadaKey as DayName;
          const jornadaData = semesterData[jornada];

          // Inicializar jornada si no existe
          if (!consolidatedData[jornada]) {
            consolidatedData[jornada] = this.createEmptyJornadaData();
          }

          const currentJornadaData = consolidatedData[jornada]!;

          // Consolidar indicadores de riesgo
          currentJornadaData['indicador-riesgo'] =
            this.consolidateRiskIndicators(
              currentJornadaData['indicador-riesgo'],
              jornadaData['indicador-riesgo'],
            );

          // Consolidar áreas
          currentJornadaData.areas = this.consolidateAreas(
            currentJornadaData.areas,
            jornadaData.areas,
          );

          // Consolidar carreras con campo jornada
          currentJornadaData.carreras = this.consolidateCareers(
            currentJornadaData.carreras,
            jornadaData.carreras.map((career) => ({
              ...career,
              jornada,
            })),
            jornada,
          );
        }
      }
    }

    return consolidatedData;
  }
}
